import { EnhancedPlan } from "../pages/SubscriptionPage/types";
import { SubscriptionStatus } from "../contexts/AuthContext";

/**
 * Formats price with currency and interval
 */
export const formatPrice = (
    price: number,
    currency: string,
    interval: string
): string => {
    if (price === 0) return "Free";

    const formattedPrice = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
        minimumFractionDigits: price % 1 === 0 ? 0 : 2,
    }).format(price);

    return formattedPrice + (interval ? ` / ${interval}` : "");
};

/**
 * Get status badge properties based on subscription status
 */
export const getSubscriptionStatusProperties = (
    status?: SubscriptionStatus | null
) => {
    if (!status) return { color: "bg-gray-100 text-gray-800", text: "Unknown" };

    switch (status) {
        case "ACTIVE":
            return { color: "bg-green-100 text-green-800", text: "Active" };
        case "SUSPENDED":
            return {
                color: "bg-yellow-100 text-yellow-800",
                text: "Suspended",
            };
        case "CANCELLED":
            return { color: "bg-red-100 text-red-800", text: "Cancelled" };
        case "EXPIRED":
            return { color: "bg-gray-100 text-gray-800", text: "Expired" };
        case "APPROVED":
            return { color: "bg-blue-100 text-blue-800", text: "Approved" };
        case "NONE":
            return { color: "bg-gray-100 text-gray-800", text: "None" };
        default:
            return { color: "bg-gray-100 text-gray-800", text: "Unknown" };
    }
};

/**
 * Calculate savings percentage between monthly and yearly pricing
 */
export const calculateYearlySavings = (
    monthlyPrice: number,
    yearlyPrice: number
): number => {
    const monthlyCostForYear = monthlyPrice * 12;
    const savings = monthlyCostForYear - yearlyPrice;
    return Math.round((savings / monthlyCostForYear) * 100);
};

/**
 * Helper to check if a plan matches the current subscription
 */
export const isCurrentPlan = (
    plan: EnhancedPlan,
    currentPlanId?: string,
    hasActiveSubscription?: boolean
): boolean => {
    return (
        !!currentPlanId && plan.id === currentPlanId && !!hasActiveSubscription
    );
};
